import { type FocusEvent, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';
import {
  DATE_DISPLAY_PLACEHOLDER,
  type DateCommitValue,
  formatDateFieldDisplay,
  formatDateInputDisplay,
  isEmptyDateDisplay,
  parseDateInputValue,
  resolveDateBlurCommitValue,
  resolveDateEnterCommitValue,
  resolveDateInputCaretPosition
} from './date-value';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export const formatDateValue = (nextValue: unknown) => {
  const parsedValue = parseDateInputValue(nextValue);

  return parsedValue ? dayjs(parsedValue).format('DD/MM/YYYY') : '';
};

const VI_WEEKDAYS = ['CN', 'HAI', 'BA', 'TƯ', 'NĂM', 'SÁU', 'BẢY'];
const MIN_CALENDAR_YEAR = 1900;
const MAX_CALENDAR_YEAR = 2100;
const CALENDAR_MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);
const CALENDAR_YEARS = Array.from(
  { length: MAX_CALENDAR_YEAR - MIN_CALENDAR_YEAR + 1 },
  (_, index) => MIN_CALENDAR_YEAR + index
);

interface ErpDateCalendarProps {
  selected?: Date;
  onSelect: (date: Date) => void;
}

const ErpDateCalendar = ({ selected, onSelect }: ErpDateCalendarProps) => {
  const selectedDateKey = selected ? dayjs(selected).format('YYYY-MM-DD') : '';
  const selectedDay = useMemo(() => (selectedDateKey ? dayjs(selectedDateKey) : null), [selectedDateKey]);
  const today = dayjs();
  const [visibleMonth, setVisibleMonth] = useState(() => (selectedDay ?? today).startOf('month'));

  useEffect(() => {
    if (selectedDay) {
      setVisibleMonth(selectedDay.startOf('month'));
    }
  }, [selectedDay]);

  const calendarDays = useMemo(() => {
    const firstDay = visibleMonth.startOf('month');
    const leadingEmptyDays = firstDay.day();
    const daysInMonth = visibleMonth.daysInMonth();
    const cellCount = Math.ceil((leadingEmptyDays + daysInMonth) / 7) * 7;

    return Array.from({ length: cellCount }, (_, index) => {
      const dayNumber = index - leadingEmptyDays + 1;
      return dayNumber >= 1 && dayNumber <= daysInMonth ? visibleMonth.date(dayNumber) : null;
    });
  }, [visibleMonth]);

  const handleMonthChange = (value: string) => {
    const nextMonth = Number(value) - 1;
    setVisibleMonth(current => current.month(nextMonth));
  };

  const handleYearChange = (value: string) => {
    const nextYear = Number(value);
    setVisibleMonth(current => current.year(nextYear));
  };

  return (
    <div className='w-[300px] bg-white text-[13px] text-black shadow-[0_8px_18px_rgba(0,0,0,0.16)]'>
      <div className='grid h-10 grid-cols-[1fr_58px_76px] items-center border-b border-gray-300 px-4'>
        <button
          type='button'
          className='h-[var(--erp-control-height)] text-left text-[13px] font-semibold hover:text-main-light'
          onClick={() => onSelect(today.toDate())}
        >
          {today.format('DD/MM/YYYY')}
        </button>
        <div className='relative flex h-[var(--erp-control-height)] items-center'>
          <select
            aria-label='Tháng'
            value={visibleMonth.month() + 1}
            onChange={event => handleMonthChange(event.target.value)}
            className='h-[var(--erp-control-height)] w-full appearance-none rounded-sm bg-transparent pl-2 pr-5 text-center text-[13px] font-semibold outline-none hover:bg-gray-50'
          >
            {CALENDAR_MONTHS.map(month => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
          <ChevronDown className='pointer-events-none absolute right-1 size-3 text-gray-500' />
        </div>
        <div className='relative flex h-[var(--erp-control-height)] items-center'>
          <select
            aria-label='Năm'
            value={visibleMonth.year()}
            onChange={event => handleYearChange(event.target.value)}
            className='h-[var(--erp-control-height)] w-full appearance-none rounded-sm bg-transparent pl-2 pr-5 text-center text-[13px] font-semibold outline-none hover:bg-gray-50'
          >
            {CALENDAR_YEARS.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <ChevronDown className='pointer-events-none absolute right-1 size-3 text-gray-500' />
        </div>
      </div>

      <div className='grid grid-cols-7 border-b border-gray-200 px-4 pb-2 pt-4'>
        {VI_WEEKDAYS.map((weekday, index) => (
          <div
            key={weekday}
            className={cn(
              'flex h-7 items-center justify-center text-[13px] font-bold',
              index === 0 && 'text-red-700',
              index === 6 && 'text-green-700'
            )}
          >
            {weekday}
          </div>
        ))}
      </div>

      <div className='grid grid-cols-7 px-4 pb-5 pt-2'>
        {calendarDays.map((date, index) => {
          const isSelected = !!date && !!selectedDay && date.isSame(selectedDay, 'day');
          const isToday = !!date && date.isSame(today, 'day');

          return (
            <div key={date?.format('YYYY-MM-DD') ?? `empty-${index}`} className='flex h-8 items-center justify-center'>
              {date && (
                <button
                  type='button'
                  className={cn(
                    'flex size-7 items-center justify-center rounded-sm text-[13px] font-semibold text-black hover:bg-main/10',
                    isToday && 'font-bold text-main',
                    isSelected && 'bg-main text-main-foreground hover:bg-main'
                  )}
                  onClick={() => onSelect(date.toDate())}
                >
                  {date.date()}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface FormDatePickerV2Props {
  id?: string;
  value?: unknown;
  onValueChange?: (value: DateCommitValue) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}

export const FormDatePickerV2 = ({
  id,
  value,
  onValueChange,
  onBlur,
  disabled = false,
  invalid = false,
  placeholder,
  className,
  inputClassName
}: FormDatePickerV2Props) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const normalizedCurrentValue = parseDateInputValue(value);
  const dateObj = normalizedCurrentValue ? dayjs(normalizedCurrentValue).toDate() : undefined;
  const [inputValue, setInputValue] = useState(() => formatDateFieldDisplay(value));
  const skipNextBlurCommitRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInputValue(formatDateFieldDisplay(value));
  }, [value]);

  const commitDisplayValue = (displayValue: string) => {
    const parsed = parseDateInputValue(displayValue);
    onValueChange?.(resolveDateBlurCommitValue(parsed ?? '', value));
    setInputValue(parsed ? formatDateFieldDisplay(parsed) : DATE_DISPLAY_PLACEHOLDER);
  };

  return (
    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
      <PopoverAnchor asChild>
        <div className={cn('relative flex w-full items-center', className)}>
          <Input
            ref={inputRef}
            id={id}
            type='text'
            disabled={disabled}
            value={inputValue}
            onChange={event => {
              const rawValue = event.target.value;
              const rawCaretPosition = event.target.selectionStart ?? rawValue.length;
              const nextDisplayValue = formatDateInputDisplay(event.target.value);
              const nextCaretPosition = resolveDateInputCaretPosition(rawValue, rawCaretPosition, nextDisplayValue);
              setInputValue(nextDisplayValue);
              window.requestAnimationFrame(() => {
                inputRef.current?.setSelectionRange(nextCaretPosition, nextCaretPosition);
              });

              if (isEmptyDateDisplay(nextDisplayValue)) {
                onValueChange?.(resolveDateBlurCommitValue('', value));
              }
            }}
            onBlur={event => {
              if (skipNextBlurCommitRef.current) {
                skipNextBlurCommitRef.current = false;
                onBlur?.(event);
                return;
              }

              commitDisplayValue(event.target.value);
              onBlur?.(event);
            }}
            onKeyDown={event => {
              const key = event.key.toLowerCase();

              if ((event.ctrlKey || event.metaKey) && key === 'a') {
                event.stopPropagation();
                return;
              }

              if (event.key === 'Enter') {
                event.preventDefault();
                const parsed = resolveDateEnterCommitValue(event.currentTarget.value);
                onValueChange?.(resolveDateBlurCommitValue(parsed, value));
                setInputValue(parsed ? formatDateFieldDisplay(parsed) : DATE_DISPLAY_PLACEHOLDER);
                skipNextBlurCommitRef.current = true;
                event.currentTarget.blur();
              }
            }}
            placeholder={disabled ? undefined : placeholder || DATE_DISPLAY_PLACEHOLDER}
            aria-invalid={invalid}
            className={cn(
              'h-[var(--erp-control-height)] w-full rounded-none border-0 border-b border-b-gray-200 bg-transparent py-0 pl-2 pr-8 text-left text-[14px] font-semibold leading-[var(--erp-control-height)] shadow-none focus-visible:border-0 focus-visible:border-b focus-visible:border-b-main focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100',
              disabled && 'font-semibold text-black',
              !normalizedCurrentValue && 'date-input-empty text-gray-400',
              inputClassName
            )}
          />
          <PopoverTrigger asChild>
            <button
              type='button'
              disabled={disabled}
              className={cn(
                'absolute right-1 inline-flex size-7 items-center justify-center disabled:cursor-not-allowed disabled:opacity-60',
                invalid ? 'text-red-500 hover:text-red-600' : 'text-main hover:text-main-dark'
              )}
              tabIndex={-1}
            >
              <CalendarIcon className='size-4' />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className='z-[2000] w-auto border-0 p-0'
        align='start'
        side='bottom'
        sideOffset={2}
        collisionPadding={8}
        avoidCollisions
      >
        <ErpDateCalendar
          selected={dateObj}
          onSelect={newValue => {
            const formattedValue = dayjs(newValue).format('YYYY-MM-DD');
            onValueChange?.(formattedValue);
            setInputValue(formatDateFieldDisplay(formattedValue));
            setDatePickerOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
